import { PickNonNullable } from '@synapex/tools/typescript';
import {
  DefaultError,
  InferDataFromTag,
  MutateOptions,
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

/* eslint-disable @typescript-eslint/no-explicit-any */

export type MergeParams<
  T1 extends (p: any) => any,
  T2 extends (p: any) => any,
> = Parameters<T1>[0] & Parameters<T2>[0];

export type UseQueryOpts<T, TData, TError = DefaultError> = T extends (
  ...args: any[]
) => UseQueryOptions<infer TQueryFnData, any, any, infer TQueryKey>
  ? Omit<
      UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
      'queryKey' | 'queryFn'
    >
  : never;

export type QueryFnData<T> = T extends (
  ...args: any[]
) => UseQueryOptions<infer TQueryFnData, any, any, any>
  ? TQueryFnData
  : never;

export type MutateOpts<T, P, TError = DefaultError> = T extends (
  ...args: any[]
) => () => Promise<infer TData>
  ? MutateOptions<
      TData,
      TError,
      void,
      {
        backup?: QueryFnData<P>;
      }
    >
  : T extends (
        ...args: any[]
      ) => (vars: infer TVariables) => Promise<infer TData>
    ? MutateOptions<
        TData,
        TError,
        TVariables,
        {
          backup?: QueryFnData<P>;
        }
      >
    : never;

export type UseMutationOpts<T> =
  T extends MutateOptions<
    infer TData,
    infer TError,
    infer TVariables,
    infer TContext
  >
    ? Omit<
        UseMutationOptions<TData, TError, TVariables, TContext>,
        'mutationKey' | 'mutationFn' | 'onMutate'
      >
    : never;

export const useMutationSuccessful = <
  TQueryFnData = unknown,
  TTaggedQueryKey extends QueryKey = QueryKey,
  TInferredQueryFnData = InferDataFromTag<TQueryFnData, TTaggedQueryKey>,
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
>(
  {
    queryKey,
    mutationKey,
    mutationFn,
    successful,
  }: {
    queryKey: TTaggedQueryKey;
    successful: (
      data: TInferredQueryFnData,
      result: TData,
    ) => TInferredQueryFnData;
  } & PickNonNullable<
    UseMutationOptions<TData, TError, TVariables, { backup?: TQueryFnData }>,
    'mutationKey' | 'mutationFn'
  >,
  {
    onSettled, //invalidate
    onSuccess, //set result
    ...opts
  }: Omit<
    UseMutationOptions<TData, TError, TVariables, { backup?: TQueryFnData }>,
    'onMutate' | 'mutationKey' | 'mutationFn'
  > = {},
): UseMutationResult<TData, TError, TVariables, { backup?: TQueryFnData }> => {
  const client = useQueryClient();

  return useMutation({
    ...opts,
    mutationKey,
    mutationFn,
    onSettled(...args) {
      void client.invalidateQueries({
        queryKey,
      });

      if (onSettled) {
        onSettled(...args);
      }
    },
    onSuccess(result, ...args) {
      //@ts-expect-error generic
      client.setQueryData(queryKey, (data) =>
        //@ts-expect-error generic
        data ? successful(data, result) : data,
      );

      if (onSuccess) {
        onSuccess(result, ...args);
      }
    },
  });
};

export const useMutationOptimistic = <
  TQueryFnData = unknown,
  TTaggedQueryKey extends QueryKey = QueryKey,
  TInferredQueryFnData = InferDataFromTag<TQueryFnData, TTaggedQueryKey>,
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
>(
  {
    queryKey,
    mutationKey,
    mutationFn,
    variablesValid = () => true,
    optimistic,
  }: {
    queryKey: TTaggedQueryKey;
    variablesValid?: (v: TVariables) => boolean;
    optimistic: (
      data: TInferredQueryFnData,
      variables: TVariables,
    ) => TInferredQueryFnData;
  } & PickNonNullable<
    UseMutationOptions<TData, TError, TVariables, { backup?: TQueryFnData }>,
    'mutationKey' | 'mutationFn'
  >,
  {
    onSettled, //invalidate
    onError, //restore backup
    ...opts
  }: Omit<
    UseMutationOptions<TData, TError, TVariables, { backup?: TQueryFnData }>,
    'onMutate' | 'mutationKey' | 'mutationFn'
  > = {},
): UseMutationResult<TData, TError, TVariables, { backup?: TQueryFnData }> => {
  const client = useQueryClient();

  return useMutation({
    ...opts,
    mutationKey,
    mutationFn,
    //@ts-expect-error generic
    onMutate(variables) {
      if (!variablesValid(variables)) {
        return { backup: undefined };
      }

      const backup = client.getQueryData(queryKey);

      // console.log({ queryKey, backup });

      if (backup) {
        //@ts-expect-error generic
        client.setQueryData(queryKey, (data) => {
          if (!data) {
            return data;
          }
          //@ts-expect-error generic
          const optimisticData = optimistic(data, variables);

          // console.log({ queryKey, variables, optimisticData });

          return optimisticData;
        });
      }

      return { backup };
    },
    onSettled(...args) {
      void client.invalidateQueries({
        queryKey,
      });

      if (onSettled) {
        onSettled(...args);
      }
    },

    onError: (error, variables, context) => {
      if (context?.backup) {
        //@ts-expect-error generic
        client.setQueryData(queryKey, context.backup);
      }

      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};
